import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import {
  Mic, FileText, Brain, Shield, Clock, Stethoscope, Languages,
  CheckCircle2, ArrowRight, Sparkles, MessageSquare, Copy, Volume2,
  ChevronDown, Zap, Lock, Eye
} from 'lucide-react'

/* ───── scroll-fade observer ───── */
function useReveal() {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { el.classList.add('revealed'); obs.unobserve(el) } },
      { threshold: 0.15 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])
  return ref
}
function Reveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useReveal()
  return (
    <div ref={ref} className={`reveal-up ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  )
}

/* ───── Phone Mockup ───── */
function PhoneMockup({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`relative mx-auto ${className}`} style={{ width: 280 }}>
      {/* Phone frame */}
      <div className="rounded-[2.5rem] border-[8px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden">
        {/* Notch */}
        <div className="relative">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-gray-900 rounded-b-2xl z-10" />
        </div>
        {/* Screen */}
        <div className="bg-white rounded-[2rem] overflow-hidden" style={{ height: 520 }}>
          {children}
        </div>
      </div>
    </div>
  )
}

/* ───── Laptop Mockup ───── */
function LaptopMockup({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`relative mx-auto ${className}`} style={{ maxWidth: 640 }}>
      {/* Screen */}
      <div className="rounded-t-xl border-[6px] border-gray-800 bg-gray-800 shadow-2xl overflow-hidden">
        <div className="bg-white" style={{ height: 380 }}>
          {children}
        </div>
      </div>
      {/* Base */}
      <div className="relative">
        <div className="bg-gray-700 h-4 rounded-b-lg mx-auto" style={{ width: '110%', marginLeft: '-5%' }} />
        <div className="bg-gray-600 h-1.5 rounded-b-md mx-auto" style={{ width: '30%' }} />
      </div>
    </div>
  )
}

/* ───── Fake App Screens ───── */
function RecordingScreen() {
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-indigo-500 text-white px-4 pt-8 pb-4">
        <div className="text-lg font-semibold">Dr. Scribey</div>
        <div className="text-indigo-200 text-xs mt-0.5">Recording encounter...</div>
      </div>
      {/* Patient */}
      <div className="px-4 py-3 bg-white border-b border-gray-100">
        <div className="text-sm font-medium text-gray-900">Johnson, Michael · 03/15/1958</div>
        <div className="text-xs text-gray-400">Right Total Knee Arthroplasty</div>
      </div>
      {/* Waveform */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="flex items-end gap-1 mb-6" style={{ height: 80 }}>
          {[20,35,55,40,65,80,50,70,45,60,30,55,75,40,50,65,35,55,70,45,30,60,80,55,40].map((h, i) => (
            <div key={i} className="w-1.5 bg-indigo-400 rounded-full animate-pulse" style={{ height: `${h}%`, animationDelay: `${i * 80}ms` }} />
          ))}
        </div>
        <div className="text-3xl font-mono text-gray-900 mb-1">03:24</div>
        <div className="flex items-center gap-1.5 text-sm text-green-600">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          Listening
        </div>
      </div>
      {/* Live transcript */}
      <div className="px-4 pb-3">
        <div className="bg-gray-100 rounded-xl p-3 text-xs text-gray-600 leading-relaxed">
          <span className="text-gray-400">Live: </span>
          "...standard midline incision, medial parapatellar approach. Osteophytes were removed from the medial and lateral..."
        </div>
      </div>
      {/* Controls */}
      <div className="px-4 pb-6 flex items-center justify-center gap-6">
        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
          <Volume2 className="w-5 h-5 text-gray-500" />
        </div>
        <div className="w-16 h-16 rounded-full bg-rose-500 flex items-center justify-center shadow-lg shadow-rose-200">
          <Mic className="w-7 h-7 text-white" />
        </div>
        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
          <MessageSquare className="w-5 h-5 text-gray-500" />
        </div>
      </div>
    </div>
  )
}

function NoteScreen() {
  return (
    <div className="h-full flex flex-col text-left">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900">Johnson, Michael</div>
          <div className="text-xs text-gray-400">Right Total Knee Arthroplasty</div>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 bg-emerald-500 rounded-full" />
          <span className="text-xs text-emerald-600 font-medium">Draft</span>
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-gray-100 px-5 gap-4 text-xs">
        <div className="py-2 border-b-2 border-indigo-500 text-indigo-600 font-medium">Note</div>
        <div className="py-2 text-gray-400">Transcript</div>
        <div className="py-2 text-gray-400">Codes</div>
        <div className="py-2 text-gray-400">Chat</div>
      </div>
      {/* Note content */}
      <div className="flex-1 overflow-hidden px-5 py-3 text-xs text-gray-700 leading-relaxed space-y-3">
        <div>
          <div className="font-semibold text-gray-900 text-[11px] uppercase tracking-wider mb-1">Preoperative Diagnosis</div>
          <div>Right knee osteoarthritis, tricompartmental</div>
        </div>
        <div>
          <div className="font-semibold text-gray-900 text-[11px] uppercase tracking-wider mb-1">Procedure</div>
          <div>Right total knee arthroplasty</div>
        </div>
        <div>
          <div className="font-semibold text-gray-900 text-[11px] uppercase tracking-wider mb-1">Operative Details</div>
          <div>The patient was brought to the operating room and placed supine on the table. General anesthesia was administered. A standard midline incision was made with a medial parapatellar approach. The patella was everted...</div>
        </div>
        <div>
          <div className="font-semibold text-gray-900 text-[11px] uppercase tracking-wider mb-1">Implants</div>
          <div>Stryker Triathlon CR femoral component size 5, tibial baseplate size 4...</div>
        </div>
      </div>
      {/* Action bar */}
      <div className="px-5 py-3 border-t border-gray-100 flex items-center gap-2">
        <button className="flex-1 bg-indigo-500 text-white text-xs font-medium py-2 rounded-lg flex items-center justify-center gap-1">
          <Copy className="w-3 h-3" /> Copy to EMR
        </button>
        <button className="px-3 py-2 bg-gray-100 text-gray-600 text-xs rounded-lg">
          <Mic className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}

/* ───── Main Landing Page ───── */
/* ───── Demo examples data ───── */
const DEMO_EXAMPLES = [
  {
    tab: 'Orthopedics',
    specialty: 'Office Visit',
    time: '~30 seconds',
    dictation: `"Alright this is Mrs. Patterson, 52, seeing her for right shoulder pain. Been going on about three months, worse with overhead stuff. She's a teacher so she's reaching up at the board all day. Tried ibuprofen, didn't help much. On exam she's got positive Neer's and Hawkins, painful arc midrange, good strength though. Got an MRI showing a partial thickness tear of the supraspinatus. I'm gonna start her on PT, give her a subacromial injection today, and see her back in six weeks."`,
    sections: [
      { title: 'HPI', text: '52-year-old female presenting with 3-month history of right shoulder pain. Pain is exacerbated by overhead activities. Patient is a teacher and reports difficulty reaching overhead at work. She has trialed ibuprofen with minimal relief. No prior injections. No history of trauma.' },
      { title: 'Examination', text: 'Right shoulder: Active ROM painful in midrange arc. Positive Neer impingement sign. Positive Hawkins-Kennedy test. Supraspinatus strength 5/5. Infraspinatus and subscapularis strength intact. Neurovascularly intact distally.' },
      { title: 'Imaging', text: 'MRI right shoulder: Partial-thickness articular-sided tear of the supraspinatus tendon. No full-thickness component. Acromion morphology type II.' },
      { title: 'Assessment', text: 'Right shoulder impingement syndrome with partial-thickness rotator cuff tear (supraspinatus).' },
      { title: 'Plan', text: '1. Subacromial corticosteroid injection performed today\n2. Physical therapy referral — rotator cuff strengthening protocol\n3. Activity modification — avoid repetitive overhead reaching\n4. Follow up in 6 weeks to reassess\n5. Discussed surgical options if conservative management fails' },
    ],
    highlight: '"No prior injections," "no history of trauma," acromion morphology, activity modification, surgical discussion',
    highlightLabel: 'AI added clinically relevant details you didn\'t say',
  },
  {
    tab: 'Ophthalmology',
    specialty: 'Post-Op Visit',
    time: '~20 seconds',
    dictation: `"This is Mr. Chen, day one post-op after cataract surgery on the left eye. He says his vision is already better. No pain, no flashes or floaters. On exam the wound is sealed, chamber is deep and quiet, lens is centered. Pressure is 14. I told him to keep using the drops as directed and I'll see him in a week."`,
    sections: [
      { title: 'HPI', text: 'Patient presents for post-operative day 1 evaluation following phacoemulsification with posterior chamber intraocular lens implantation, left eye. Reports subjective improvement in vision. Denies pain, photopsia, or floaters.' },
      { title: 'Examination', text: 'Left eye:\nIntraocular pressure: 14 mmHg\nSlit lamp: Clear corneal incision well-sealed, Seidel negative. Anterior chamber deep and quiet, no cells or flare. PCL well-centered in the capsular bag.' },
      { title: 'Assessment', text: 'Status post phacoemulsification with PCIOL implantation, left eye — post-operative day 1. Progressing well.' },
      { title: 'Plan', text: '1. Continue current post-operative drop regimen (antibiotic, steroid, NSAID) as prescribed\n2. Wear protective shield at night\n3. Follow up in 1 week\n4. Precautions reviewed: no heavy lifting, no rubbing eye, no swimming' },
    ],
    highlight: '"Seidel negative," "no cells or flare," "photopsia," post-op precautions, drop regimen — standard post-cataract documentation',
    highlightLabel: 'AI translates your shorthand and fills in standard-of-care documentation',
  },
  {
    tab: 'General Surgery',
    specialty: 'Op Note',
    time: '~15 seconds',
    dictation: `"Do my usual laparoscopic cholecystectomy but we had some dense adhesions at the gallbladder fundus, had to do a subtotal. Critical view was obtained. Used Endo GIA on the cystic duct because the clip wouldn't fit."`,
    sections: [
      { title: 'Procedure', text: 'Laparoscopic Subtotal Cholecystectomy' },
      { title: 'Operative Details', text: 'The patient was placed supine on the operating table. General anesthesia was induced. A time-out was performed. The abdomen was prepped and draped in standard sterile fashion.\n\nPneumoperitoneum was established via Veress needle technique. A 12mm trocar was placed at the umbilicus. Under direct visualization, three additional ports were placed in standard positions. The camera was advanced.\n\nDense adhesions were encountered at the gallbladder fundus and dissected sharply and bluntly. Due to severe inflammation, a subtotal cholecystectomy was performed. The hepatocystic triangle was dissected and the critical view of safety was obtained, with clear identification of the cystic duct and cystic artery. The cystic artery was clipped and divided. The cystic duct was too large for clip application; it was divided using an Endo GIA stapler.\n\nThe gallbladder was dissected from the liver bed using electrocautery. Hemostasis was achieved. The specimen was placed in an endocatch bag and removed through the umbilical port.\n\nAll ports were removed under direct visualization. Fascia was closed at the umbilical site. Skin was closed with subcuticular sutures and dermabond. The patient was extubated and transferred to recovery in stable condition.' },
      { title: 'Findings', text: 'Dense adhesions at gallbladder fundus consistent with chronic cholecystitis. Cystic duct dilated, unable to accommodate standard clip application. No bile duct injury identified. No stones spilled.' },
      { title: 'Disposition', text: 'To PACU in stable condition. EBL minimal. All counts correct.' },
    ],
    highlight: 'From 15 seconds of "my usual but..." to a complete operative report — prep, ports, dissection, closure, findings, disposition',
    highlightLabel: 'AI knows your procedures and writes the parts you didn\'t need to say',
  },
]

function DemoSection() {
  const [activeTab, setActiveTab] = useState(0)
  const example = DEMO_EXAMPLES[activeTab]

  return (
    <section id="demo" className="py-20 lg:py-24 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <div className="text-center mb-10">
            <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">See It In Action</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">From recording to finished note in seconds</h2>
          </div>
        </Reveal>

        {/* Specialty tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-gray-100 rounded-xl p-1 gap-1">
            {DEMO_EXAMPLES.map((ex, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                  activeTab === i
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {ex.tab}
              </button>
            ))}
          </div>
        </div>

        {/* Specialty + note type badge */}
        <div className="flex justify-center mb-6">
          <span className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 text-xs font-medium px-3 py-1.5 rounded-full">
            <Stethoscope className="w-3.5 h-3.5" />
            {example.tab} · {example.specialty}
          </span>
        </div>

        {/* Before / After grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="h-full">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Mic className="w-3.5 h-3.5" /> What you said
              <span className="text-gray-300 font-normal normal-case">{example.time}</span>
            </div>
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 h-[calc(100%-2rem)]">
              <p className="text-gray-600 text-sm leading-relaxed italic">{example.dictation}</p>
            </div>
          </div>
          <div className="h-full">
            <div className="text-xs font-semibold text-indigo-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> What Dr. Scribey generated
            </div>
            <div className="bg-white rounded-2xl p-6 border-2 border-indigo-100 shadow-sm h-[calc(100%-2rem)] overflow-y-auto max-h-[500px]">
              <div className="text-sm space-y-3">
                {example.sections.map((s, i) => (
                  <div key={i}>
                    <div className="font-semibold text-gray-900 text-xs uppercase tracking-wider mb-1">{s.title}</div>
                    <p className="text-gray-700 whitespace-pre-line">{s.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Highlight callout */}
        <div className="mt-8 text-center">
          <div className="inline-flex items-start gap-3 bg-indigo-50 rounded-2xl px-5 py-3 text-sm max-w-3xl text-left">
            <Sparkles className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
            <span className="text-gray-600">
              <strong className="text-indigo-700">{example.highlightLabel}:</strong>{' '}
              <span className="text-gray-500">{example.highlight}</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ───── Animated walkthrough ───── */
function AnimatedWalkthrough() {
  const [step, setStep] = useState(0)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const STEPS = [
    { label: 'Record', duration: 3000 },
    { label: 'Processing', duration: 2000 },
    { label: 'Note Ready', duration: 4000 },
  ]

  useEffect(() => {
    const cycle = () => {
      setStep(0)
      let s = 0
      const advance = () => {
        s++
        if (s >= STEPS.length) { s = 0 }
        setStep(s)
      }
      intervalRef.current = setInterval(advance, 3000)
    }
    cycle()
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [])

  // Waveform bars animation
  const WaveformBars = () => (
    <div className="flex items-end gap-[3px] h-10 justify-center">
      {Array.from({ length: 24 }).map((_, i) => (
        <div
          key={i}
          className="w-[3px] bg-indigo-400 rounded-full"
          style={{
            animation: step === 0 ? `waveform 0.8s ease-in-out ${i * 0.05}s infinite alternate` : 'none',
            height: step === 0 ? undefined : '4px',
            opacity: step === 0 ? 1 : 0.3,
          }}
        />
      ))}
    </div>
  )

  const noteLines = [
    'HPI: 67-year-old male presenting with...',
    'Examination: Right knee — ROM 0-120°...',
    'Assessment: Status post right TKA...',
    'Plan: 1. Continue PT protocol...',
  ]

  return (
    <section className="py-24 px-4 sm:px-6 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl mx-auto">
        <Reveal>
          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">How It Feels</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">Record. Generate. Done.</h2>
            <p className="text-gray-500 mt-3 max-w-lg mx-auto">No buttons to press, no templates to pick. Just talk and your note writes itself.</p>
          </div>
        </Reveal>

        {/* Step indicators */}
        <div className="flex justify-center gap-2 mb-10">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-500 ${
                step === i ? 'bg-indigo-500 text-white scale-110' : step > i ? 'bg-indigo-100 text-indigo-400' : 'bg-gray-100 text-gray-400'
              }`}>
                {i + 1}
              </div>
              <span className={`text-sm font-medium transition-colors ${step === i ? 'text-gray-900' : 'text-gray-400'}`}>
                {s.label}
              </span>
              {i < STEPS.length - 1 && <div className="w-12 h-px bg-gray-200 mx-1" />}
            </div>
          ))}
        </div>

        {/* Phone mockup with animated content */}
        <div className="flex justify-center">
          <div className="w-[280px]">
            <PhoneMockup>
              <div className="h-full flex flex-col">
                {/* Status bar */}
                <div className="px-4 pt-3 pb-2 border-b border-gray-100">
                  <div className="text-[10px] font-medium text-gray-500 text-center">
                    {step === 0 && '● Recording...'}
                    {step === 1 && '⟳ Generating note...'}
                    {step === 2 && '✓ Note ready'}
                  </div>
                </div>

                <div className="flex-1 flex items-center justify-center px-4 py-6">
                  {/* Step 0: Recording waveform */}
                  {step === 0 && (
                    <div className="text-center w-full">
                      <WaveformBars />
                      <div className="mt-4 text-xs text-gray-400 italic">
                        "Do my usual right total knee..."
                      </div>
                      <div className="mt-3 text-[10px] text-red-400 font-medium flex items-center justify-center gap-1">
                        <div className="w-2 h-2 bg-red-400 rounded-full animate-pulse" /> 0:14
                      </div>
                    </div>
                  )}

                  {/* Step 1: Processing spinner */}
                  {step === 1 && (
                    <div className="text-center">
                      <div className="w-12 h-12 border-3 border-indigo-200 border-t-indigo-500 rounded-full animate-spin mx-auto" />
                      <p className="text-xs text-gray-400 mt-4">AI is writing your note...</p>
                    </div>
                  )}

                  {/* Step 2: Note appears */}
                  {step === 2 && (
                    <div className="w-full space-y-2">
                      {noteLines.map((line, i) => (
                        <div
                          key={i}
                          className="text-[11px] text-gray-700 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100"
                          style={{
                            animation: `fadeSlideIn 0.4s ease ${i * 0.15}s both`,
                          }}
                        >
                          {line}
                        </div>
                      ))}
                      <div
                        className="flex items-center justify-center gap-2 mt-3"
                        style={{ animation: 'fadeSlideIn 0.4s ease 0.7s both' }}
                      >
                        <button className="bg-indigo-500 text-white text-[10px] font-medium px-3 py-1.5 rounded-lg">
                          Copy to EMR
                        </button>
                        <button className="bg-gray-100 text-gray-600 text-[10px] font-medium px-3 py-1.5 rounded-lg">
                          Edit
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </PhoneMockup>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes waveform {
          from { height: 4px; }
          to { height: 32px; }
        }
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  )
}

function PricingCards({ navigate }: { navigate: (path: string) => void }) {
  const [isAnnual, setIsAnnual] = React.useState(false)

  const coreFeatures = [
    'Unlimited encounters & notes',
    'All office visit note types',
    'Voice-based editing',
    'AI clinical assistant & chat',
    'Cross-visit patient memory',
    'Style learning',
    'Patient intake module',
    'Multilingual (11 languages)',
    'Chrome extension / EMR push',
    'HIPAA compliant · BAA included',
  ]

  const proFeatures = [
    'Op notes (dictate → billable note)',
    'ICD-10 & CPT coding suggestions',
    'E/M level & revenue analysis',
    'Prior-auth guidance',
    'Priority support',
  ]

  return (
    <div>
      {/* Annual toggle */}
      <Reveal>
        <div className="flex items-center justify-center gap-3 mb-10">
          <span className={`text-sm font-medium ${!isAnnual ? 'text-gray-900' : 'text-gray-400'}`}>Monthly</span>
          <button
            onClick={() => setIsAnnual(a => !a)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isAnnual ? 'bg-indigo-500' : 'bg-gray-200'}`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${isAnnual ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
          <span className={`text-sm font-medium ${isAnnual ? 'text-gray-900' : 'text-gray-400'}`}>
            Annual <span className="text-emerald-500 font-semibold">(save 20%)</span>
          </span>
        </div>
      </Reveal>

      <div className="grid sm:grid-cols-2 gap-6 items-start">
        {/* Core Card */}
        <Reveal delay={100}>
          <div className="bg-white rounded-3xl border-2 border-indigo-200 p-8 shadow-xl shadow-indigo-50 relative overflow-hidden h-full flex flex-col">
            <div className="absolute top-0 right-0 bg-indigo-500 text-white text-xs font-bold px-4 py-1 rounded-bl-xl">MOST POPULAR</div>
            <div className="text-sm font-medium text-indigo-500 uppercase tracking-wide mb-4">Core</div>
            <div className="text-lg font-semibold text-gray-700 mb-4">AI Clinical Scribe</div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-5xl font-bold text-gray-900">{isAnnual ? '$79' : '$99'}</span>
              <span className="text-gray-400 text-lg">/mo</span>
            </div>
            {isAnnual && <p className="text-gray-400 text-sm mb-1">billed annually ($948/yr)</p>}
            {!isAnnual && <p className="text-gray-400 text-sm mb-1">or $79/mo billed annually</p>}
            <p className="text-gray-400 text-sm mb-6">per provider</p>
            <ul className="space-y-3 text-sm text-gray-600 mb-8 flex-1">
              {coreFeatures.map(f => (
                <li key={f} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate('/register')}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-3.5 rounded-xl transition shadow-lg shadow-indigo-200 mb-2 text-base"
            >
              Try 10 Free
            </button>
            <p className="text-xs text-gray-400 text-center">No credit card required</p>
          </div>
        </Reveal>

        {/* Pro Card */}
        <Reveal delay={200}>
          <div className="bg-white rounded-3xl border-2 border-indigo-500 p-8 shadow-xl shadow-indigo-100 relative overflow-hidden h-full flex flex-col">
            <div className="text-sm font-medium text-indigo-500 uppercase tracking-wide mb-4">Pro</div>
            <div className="text-lg font-semibold text-gray-700 mb-4">Surgical Intelligence</div>
            <div className="flex items-baseline gap-1 mb-1">
              <span className="text-5xl font-bold text-gray-900">{isAnnual ? '$169' : '$199'}</span>
              <span className="text-gray-400 text-lg">/mo</span>
            </div>
            {isAnnual && <p className="text-gray-400 text-sm mb-1">billed annually ($2,028/yr)</p>}
            {!isAnnual && <p className="text-gray-400 text-sm mb-1">or $169/mo billed annually</p>}
            <p className="text-gray-400 text-sm mb-6">per provider</p>
            <p className="text-sm font-semibold text-gray-700 mb-3">Everything in Core, plus:</p>
            <ul className="space-y-3 text-sm text-gray-600 mb-4 flex-1">
              {proFeatures.map(f => (
                <li key={f} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-500 italic mb-6 bg-indigo-50 rounded-xl p-3">
              "One properly coded procedure covers the month"
            </p>
            <button
              onClick={() => navigate('/register')}
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-white font-medium py-3.5 rounded-xl transition shadow-lg shadow-indigo-200 mb-2 text-base"
            >
              Try 10 Free
            </button>
            <p className="text-xs text-gray-400 text-center">No credit card required</p>
          </div>
        </Reveal>
      </div>
        <p className="text-center text-sm text-gray-400 mt-6">
          Group pricing available for practices with 5+ providers
        </p>
    </div>
  )
}

export function LandingPage() {
  const navigate = useNavigate()

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* ── Inline styles for animations ── */}
      <style>{`
        .reveal-up {
          opacity: 0;
          transform: translateY(30px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .reveal-up.revealed {
          opacity: 1;
          transform: translateY(0);
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        .float { animation: float 6s ease-in-out infinite; }
        .float-delayed { animation: float 6s ease-in-out 2s infinite; }
        @keyframes gradient-shift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .gradient-bg {
          background: linear-gradient(-45deg, #e0e7ff, #ede9fe, #e0e7ff, #c7d2fe);
          background-size: 400% 400%;
          animation: gradient-shift 12s ease infinite;
        }
        .glass {
          background: rgba(255,255,255,0.7);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }
      `}</style>

      {/* ── Nav ── */}
      <nav className="fixed top-0 w-full glass border-b border-gray-200/50 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center">
              <Stethoscope className="w-4 h-4 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">Dr. Scribey</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-gray-600">
            <a href="#features" className="hover:text-gray-900 transition">Features</a>
            <a href="#how-it-works" className="hover:text-gray-900 transition">How It Works</a>
            <a href="#security" className="hover:text-gray-900 transition">Security</a>
            <a href="#pricing" className="hover:text-gray-900 transition">Pricing</a>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/login')} className="text-sm text-gray-600 hover:text-gray-900 transition">
              Log In
            </button>
            <button onClick={() => navigate('/register')} className="bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition shadow-sm shadow-indigo-200">
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative pt-28 pb-8 px-4 sm:px-6 overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 gradient-bg opacity-40" />
        <div className="absolute top-20 -right-20 w-72 h-72 bg-indigo-200 rounded-full blur-3xl opacity-30" />
        <div className="absolute bottom-0 -left-20 w-96 h-96 bg-violet-200 rounded-full blur-3xl opacity-20" />

        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: copy */}
            <div className="text-center lg:text-left">
              <Reveal>
                <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 text-indigo-700 text-sm font-medium px-3 py-1.5 rounded-full mb-6">
                  <Brain className="w-4 h-4" />
                  The AI scribe that remembers your patients
                </div>
              </Reveal>
              <Reveal delay={100}>
                <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold tracking-tight text-gray-900 leading-[1.1] mb-6">
                  Focus on your patient.<br />
                  <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">We'll handle the note.</span>
                </h1>
              </Reveal>
              <Reveal delay={200}>
                <p className="text-lg text-gray-500 max-w-xl mx-auto lg:mx-0 mb-8 leading-relaxed">
                  Dr. Scribey listens to the visit, writes the note in your style, and remembers every patient — last visit's findings and plan carry into today's draft. Finish charting before you leave the office.
                </p>
              </Reveal>
              <Reveal delay={300}>
                <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                  <button onClick={() => navigate('/register')} className="bg-indigo-500 hover:bg-indigo-600 text-white font-medium px-7 py-3.5 rounded-xl text-lg transition flex items-center justify-center gap-2 shadow-lg shadow-indigo-200">
                    Try 10 Encounters Free <ArrowRight className="w-5 h-5" />
                  </button>
                  <a href="#demo" className="border border-gray-200 hover:border-gray-300 bg-white/70 text-gray-700 font-medium px-7 py-3.5 rounded-xl text-lg transition text-center">
                    See How It Works
                  </a>
                </div>
                <p className="text-sm text-gray-400 mt-4">No credit card required · HIPAA compliant · Set up in 2 minutes</p>
              </Reveal>
            </div>
            {/* Right: device mockups */}
            <Reveal delay={400} className="hidden lg:block">
              <div className="relative">
                <div className="float">
                  <LaptopMockup>
                    <NoteScreen />
                  </LaptopMockup>
                </div>
                <div className="absolute -bottom-8 -left-12 float-delayed">
                  <PhoneMockup className="scale-[0.55] origin-bottom-left">
                    <RecordingScreen />
                  </PhoneMockup>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Mobile mockup */}
          <Reveal delay={300} className="lg:hidden mt-12 flex justify-center">
            <PhoneMockup>
              <RecordingScreen />
            </PhoneMockup>
          </Reveal>

          {/* Scroll indicator */}
          <div className="flex justify-center mt-12 animate-bounce">
            <ChevronDown className="w-5 h-5 text-gray-300" />
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section className="relative border-y border-gray-100 bg-white py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { num: '<60s', label: 'Dictation to draft' },
                { num: '80+', label: 'Specialties covered' },
                { num: '35+', label: 'EMRs supported' },
                { num: '11', label: 'Intake languages' },
              ].map(s => (
                <div key={s.label}>
                  <div className="text-3xl font-bold tracking-tight text-gray-900">{s.num}</div>
                  <div className="text-sm text-gray-500 mt-1.5">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Trust strip (compliance + EMR compatibility) ── */}
      <section className="py-8 px-4 sm:px-6 bg-gray-50 border-b border-gray-100">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-x-10 gap-y-3 text-sm text-gray-500">
          <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-gray-400" /><span>HIPAA compliant · BAA included</span></div>
          <div className="hidden sm:block w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-gray-400" /><span>Encrypted in transit &amp; at rest</span></div>
          <div className="hidden sm:block w-px h-4 bg-gray-200" />
          <div className="flex items-center gap-2"><Copy className="w-4 h-4 text-gray-400" /><span>Works with any EMR — nothing to integrate</span></div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="py-20 lg:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">How It Works</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">Clinical notes in three simple steps</h2>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                step: '01',
                icon: Mic,
                title: 'Record',
                desc: 'Start a session and talk to your patient naturally. Dr. Scribey listens in the background — ambient or dictation, your choice.',
              },
              {
                step: '02',
                icon: Sparkles,
                title: 'Generate',
                desc: 'Get a complete clinical note in seconds. Every sentence traces back to your recording. Built from your style, not a generic template.',
              },
              {
                step: '03',
                icon: Copy,
                title: 'Transfer',
                desc: 'Copy your note to any EMR with one click. No integrations to configure, no IT department needed.',
              },
            ].map((s, i) => (
              <Reveal key={s.step} delay={i * 150}>
                <div className="relative bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full">
                  <div className="text-6xl font-bold text-gray-50 absolute top-4 right-6">{s.step}</div>
                  <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center mb-5">
                    <s.icon className="w-6 h-6 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-500 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Animated Walkthrough ── */}
      <AnimatedWalkthrough />

      {/* ── Feature: Voice Commands + Mockup ── */}
      <section className="py-20 lg:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div>
                <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">Agentic Documentation</p>
                <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-4">
                  "My usual TKA, but we went subvastus."
                </h2>
                <p className="text-gray-500 leading-relaxed mb-6">
                  Dr. Scribey doesn't just transcribe — it <em>understands</em>. One sentence is all it takes. It pulls your preferred template, applies the changes you mentioned, and generates a complete operative note. Try that with any other scribe.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: Brain, text: 'Learns your preferred format from your approved notes' },
                    { icon: Clock, text: 'Recalls prior visits — references earlier findings automatically' },
                    { icon: Volume2, text: 'Edit by voice — dictate revisions, don\'t type them' },
                  ].map((f, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <f.icon className="w-4 h-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-600">{f.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="space-y-4">
                <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center">
                      <Mic className="w-3 h-3 text-gray-500" />
                    </div>
                    <span className="text-xs text-gray-400 font-medium">You dictated</span>
                  </div>
                  <p className="text-gray-700 italic leading-relaxed">"My usual right total knee but we went subvastus, used Stryker Triathlon, and had to do a lateral release."</p>
                </div>
                <div className="flex justify-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-full flex items-center justify-center shadow-md">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-5 shadow-sm border-2 border-indigo-100 relative">
                  <div className="absolute -top-2 left-5 bg-indigo-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">AI GENERATED</div>
                  <div className="mt-2 text-sm text-gray-700 space-y-2">
                    <p className="font-semibold text-gray-900">OPERATIVE NOTE</p>
                    <p><span className="font-medium">Procedure:</span> Right Total Knee Arthroplasty</p>
                    <p><span className="font-medium">Approach:</span> Subvastus (quadriceps-sparing) — modified from your usual medial parapatellar</p>
                    <p><span className="font-medium">Implants:</span> Stryker Triathlon CR system</p>
                    <p><span className="font-medium">Additional:</span> Lateral retinacular release performed due to patellar maltracking</p>
                    <p className="text-gray-400 text-xs">+ 12 more sections from your template...</p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Features Grid ── */}
      <section id="features" className="py-20 lg:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">Features</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">More than just a scribe</h2>
              <p className="text-gray-500 mt-3 max-w-2xl mx-auto">The note is the starting point. The rest of the visit's paperwork comes with it.</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Brain, title: 'It learns how you write', desc: 'Every note you approve teaches it your phrasing, your format, your shorthand. First drafts start sounding like you, not a template.' },
              { icon: Sparkles, title: 'See exactly what it\'s learned', desc: 'A dashboard lists every phrase, style rule, and template it has picked up from your notes. Edit or delete any of it.' },
              { icon: Clock, title: 'It remembers last visit', desc: 'Prior findings and assessments carry into today\'s note, with interval changes tracked — no re-dictating the backstory.' },
              { icon: Mic, title: 'Edit out loud', desc: '"Add risks and benefits." "Include the implant specs." Say it, and the note updates — no typing.' },
              { icon: MessageSquare, title: 'An assistant on the note page', desc: 'Ask about this patient\'s history, or tell it to rework a section. It answers without you leaving the note.' },
              { icon: FileText, title: 'Op notes to telehealth', desc: 'SOAP notes, consults, procedures, rounding, operative notes — one tool covers every encounter you document.' },
              { icon: Zap, title: 'Coding suggestions built in', desc: 'ICD-10 and CPT suggestions for every note, grounded in what you documented. You review, you decide.' },
              { icon: FileText, title: 'Letters in one click', desc: 'Referral letters, work notes, clearance requests, patient instructions — drafted from the visit, ready to sign.' },
              { icon: Languages, title: 'Patients arrive pre-interviewed', desc: 'Send a pre-visit link: patients answer a guided interview in any of 11 languages. You get a clean English summary before you walk in.' },
            ].map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 100}>
                <div className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
                  <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center mb-4">
                    <f.icon className="w-5 h-5 text-indigo-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{f.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── AI Learning / Scribe that knows you ── */}
      <section className="py-20 lg:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal delay={100} className="order-2 lg:order-1">
              {/* AI learning visualization */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
                  <Brain className="w-4 h-4 text-indigo-500" />
                  <span className="text-sm font-semibold text-gray-900">AI Learning Dashboard</span>
                </div>
                <div className="p-5 space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-medium text-gray-600">Style Confidence</span>
                      <span className="text-xs font-bold text-indigo-600">87%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2">
                      <div className="bg-gradient-to-r from-indigo-500 to-violet-500 h-2 rounded-full" style={{ width: '87%' }} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-gray-50 rounded-xl p-3">
                      <div className="text-lg font-bold text-gray-900">24</div>
                      <div className="text-[10px] text-gray-400">Approved Notes</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <div className="text-lg font-bold text-gray-900">8</div>
                      <div className="text-[10px] text-gray-400">Phrases Learned</div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3">
                      <div className="text-lg font-bold text-gray-900">3</div>
                      <div className="text-[10px] text-gray-400">Custom Templates</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-600 mb-2">Learned Phrases</div>
                    <div className="space-y-1.5">
                      {[
                        '"The wound was closed in layers using 2-0 Vicryl and staples"',
                        '"Tourniquet time was ___ minutes"',
                        '"Patient tolerated the procedure well"',
                      ].map((p, i) => (
                        <div key={i} className="bg-indigo-50 text-indigo-700 text-xs px-3 py-1.5 rounded-lg">{p}</div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
            <Reveal className="order-1 lg:order-2">
              <div>
                <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">Continuous Learning</p>
                <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-4">An AI scribe with perfect recall that gets smarter every day</h2>
                <p className="text-gray-500 leading-relaxed mb-6">
                  Imagine a scribe who has worked alongside you for years — someone who knows your templates, your phrasing, how you close every note. Dr. Scribey builds that understanding from your first session, and refines it with every note you approve.
                </p>
                <ul className="space-y-3">
                  {[
                    'Builds a personal phrasebook from your approved notes',
                    'Creates composite templates from your patterns',
                    'Tracks your preferred structure for each note type',
                    'Full transparency — see everything it has learned',
                    'Override or adjust any learned preference',
                    'Specialty-aware — understands your field\'s conventions',
                  ].map(item => (
                    <li key={item} className="flex items-start gap-3 text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Patient Intake ── */}
      <section className="py-20 lg:py-24 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div>
                <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">Patient Intake</p>
                <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-4">Patients arrive with their history already documented</h2>
                <p className="text-gray-500 leading-relaxed mb-6">
                  Send patients a secure link before their visit. An AI-guided interview collects chief complaint, medical history, medications, allergies, and review of systems — all flowing directly into their encounter note.
                </p>
                <ul className="space-y-3">
                  {[
                    'Tap-first design — patients answer with buttons, not typing',
                    '11 languages — Spanish, Tagalog, Arabic, Vietnamese, and more',
                    'Specialty-aware questions adapt to your practice',
                    'Extracted data feeds directly into note generation',
                    'Secure token-based links with PIN protection',
                    'Works on any phone or tablet — no app download',
                  ].map(item => (
                    <li key={item} className="flex items-start gap-3 text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={200} className="flex justify-center">
              <div className="float">
                <PhoneMockup>
                  {/* Intake screen mockup */}
                  <div className="h-full flex flex-col bg-gray-50">
                    <div className="bg-indigo-500 text-white px-4 pt-8 pb-4">
                      <div className="text-lg font-semibold">Dr. Scribey</div>
                      <div className="text-indigo-200 text-xs mt-0.5">Patient Intake</div>
                    </div>
                    <div className="px-4 py-3 bg-white border-b border-gray-100">
                      <div className="text-sm font-medium text-gray-900">Williams, Sarah · 07/22/1985</div>
                      <div className="text-xs text-gray-400">Pre-visit interview</div>
                    </div>
                    <div className="flex-1 px-4 py-4 space-y-3 overflow-hidden">
                      <div className="bg-indigo-50 rounded-2xl rounded-tl-sm p-3 text-xs text-gray-700 max-w-[85%]">
                        What brings you in today?
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {['Pain or injury', 'Follow-up visit', 'New concern'].map(c => (
                          <div key={c} className="bg-white border border-indigo-200 text-indigo-700 text-[10px] font-medium px-2.5 py-1.5 rounded-full">{c}</div>
                        ))}
                      </div>
                      <div className="bg-indigo-500 text-white rounded-2xl rounded-tr-sm p-3 text-xs max-w-[75%] ml-auto">
                        Follow-up visit
                      </div>
                      <div className="bg-indigo-50 rounded-2xl rounded-tl-sm p-3 text-xs text-gray-700 max-w-[85%]">
                        Great. How has your knee been feeling since your last visit?
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {['Much better', 'About the same', 'Getting worse', 'New symptoms'].map(c => (
                          <div key={c} className="bg-white border border-indigo-200 text-indigo-700 text-[10px] font-medium px-2.5 py-1.5 rounded-full">{c}</div>
                        ))}
                      </div>
                      <div className="bg-indigo-500 text-white rounded-2xl rounded-tr-sm p-3 text-xs max-w-[75%] ml-auto">
                        Much better
                      </div>
                      <div className="bg-indigo-50 rounded-2xl rounded-tl-sm p-3 text-xs text-gray-700 max-w-[85%]">
                        That's great to hear. Are you still taking the medications from your last visit?
                      </div>
                    </div>
                    {/* Input */}
                    <div className="px-4 py-3 border-t border-gray-100 bg-white flex items-center gap-2">
                      <div className="flex-1 bg-gray-100 rounded-full px-3 py-2 text-[10px] text-gray-400">Type or tap to answer...</div>
                      <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                        <Mic className="w-3.5 h-3.5 text-white" />
                      </div>
                    </div>
                  </div>
                </PhoneMockup>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Rounding Notes ── */}
      <section className="py-20 lg:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div>
                <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">Inpatient Rounding</p>
                <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-4">
                  Round on 20 patients.<br />
                  Finish every note before lunch.
                </h2>
                <p className="text-gray-500 leading-relaxed mb-6">
                  Walk the floor, see your patients, and dictate a quick update at each bedside. Dr. Scribey remembers yesterday's plan and what you've done so far — then writes a complete progress note that captures exactly what changed. No more catching up at 9 PM.
                </p>
                <ul className="space-y-3">
                  {[
                    'Quick bedside dictation — 30 seconds per patient',
                    'Cross-visit memory recalls yesterday\'s plan and prior findings',
                    'Interval changes detected and documented automatically',
                    '"Same as yesterday except..." — AI handles the rest',
                    'Batch-review all notes between rounds',
                    'Works for hospitalists, surgeons, consultants, and residents',
                  ].map(item => (
                    <li key={item} className="flex items-start gap-3 text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={200}>
              {/* Rounding workflow visualization */}
              <div className="space-y-4">
                {/* Patient list / rounding view mockup */}
                <div className="bg-white rounded-2xl border border-gray-100 shadow-xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Stethoscope className="w-4 h-4 text-indigo-500" />
                      <span className="text-sm font-semibold text-gray-900">Morning Rounds</span>
                    </div>
                    <span className="text-xs text-gray-400">7 of 18 seen</span>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {[
                      { name: 'Martinez, R.', room: '412A', status: 'done', note: 'Progress note ready', time: '0:22' },
                      { name: 'Thompson, J.', room: '414B', status: 'done', note: 'Progress note ready', time: '0:35' },
                      { name: 'O\'Brien, K.', room: '416A', status: 'recording', note: 'Recording...', time: '0:14' },
                      { name: 'Patel, S.', room: '418B', status: 'pending', note: 'POD #2 — labs back', time: '' },
                      { name: 'Williams, D.', room: '420A', status: 'pending', note: 'New consult', time: '' },
                    ].map((pt, i) => (
                      <div key={i} className="px-5 py-3 flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          pt.status === 'done' ? 'bg-emerald-500' :
                          pt.status === 'recording' ? 'bg-red-400 animate-pulse' :
                          'bg-gray-200'
                        }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{pt.name}</span>
                            <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{pt.room}</span>
                          </div>
                          <span className="text-xs text-gray-400">{pt.note}</span>
                        </div>
                        {pt.time && (
                          <span className={`text-[10px] font-mono ${pt.status === 'recording' ? 'text-red-400' : 'text-gray-300'}`}>
                            {pt.time}
                          </span>
                        )}
                        {pt.status === 'done' && (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {/* Example dictation bubble */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                      <Mic className="w-2.5 h-2.5 text-gray-500" />
                    </div>
                    <span className="text-[10px] text-gray-400 font-medium">Room 416A · O'Brien, K.</span>
                  </div>
                  <p className="text-gray-700 text-sm italic leading-relaxed">
                    "Post-op day one right hip. She's doing well, pain controlled on PCA, PT got her up to chair this morning. Wound is clean and dry, no drainage. Pull the Foley today, advance diet, continue DVT prophylaxis."
                  </p>
                </div>
                {/* Arrow */}
                <div className="flex justify-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-violet-500 rounded-full flex items-center justify-center shadow-md">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                </div>
                {/* Generated note preview */}
                <div className="bg-white rounded-2xl p-4 shadow-sm border-2 border-indigo-100 relative">
                  <div className="absolute -top-2 left-4 bg-indigo-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">PROGRESS NOTE</div>
                  <div className="mt-2 text-xs text-gray-700 space-y-1.5">
                    <p><span className="font-semibold text-gray-900">S:</span> Patient reports pain well controlled. Tolerated PT, mobilized to chair.</p>
                    <p><span className="font-semibold text-gray-900">O:</span> POD #1 status post R total hip arthroplasty. Afebrile, VSS. Surgical site clean, dry, intact, no erythema or drainage. NV intact distally.</p>
                    <p><span className="font-semibold text-gray-900">A:</span> Progressing well post R THA. Pain controlled. Mobilizing appropriately.</p>
                    <p><span className="font-semibold text-gray-900">P:</span> D/C Foley, advance diet to regular, continue enoxaparin DVT prophylaxis, PT BID, reassess PCA tomorrow.</p>
                    <p className="text-gray-400 text-[10px] mt-2 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Generated in 8 seconds · 30-second dictation
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Why Dr. Scribey ── */}
      <section className="py-20 lg:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">Why Dr. Scribey</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">Up and running in minutes, not months</h2>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { text: 'Works with any EMR — copy, paste, done' },
                { text: 'Set up in under 2 minutes' },
                { text: 'No contracts — cancel anytime' },
                { text: 'Your data stays yours — never used for training' },
                { text: 'HIPAA compliant with BAA included' },
                { text: '360+ specialty-specific note templates' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-xl p-4 border border-gray-100">
                  <CheckCircle2 className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Before / After (3 tabbed examples) ── */}
      <DemoSection />

      {/* ── Mobile ── */}
      <section className="py-20 lg:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <Reveal>
              <div>
                <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">Works Everywhere</p>
                <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-4">Your scribe goes where you go</h2>
                <p className="text-gray-500 leading-relaxed mb-6">
                  Between patients, between rooms, between surgeries — pull out your phone and start recording. Dr. Scribey works in any browser on any device. No app to download, no IT to involve.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: Mic, text: 'Record on your iPhone or Android between patients' },
                    { icon: FileText, text: 'Review and approve notes from your desktop later' },
                    { icon: Copy, text: 'Copy to your EMR from any device' },
                    { icon: Shield, text: 'Auto-locks after inactivity — HIPAA compliant on shared devices' },
                  ].map((f, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                        <f.icon className="w-4 h-4 text-indigo-600" />
                      </div>
                      <span className="text-gray-600">{f.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={200} className="flex justify-center">
              <div className="relative">
                <div className="float">
                  <PhoneMockup>
                    <RecordingScreen />
                  </PhoneMockup>
                </div>
                {/* Desktop hint */}
                <div className="absolute -top-4 -right-8 bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-3 hidden lg:block float-delayed">
                  <div className="text-[10px] text-gray-400 font-medium mb-1">Also on desktop</div>
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-4 bg-gray-200 rounded-sm" />
                    <div className="w-8 h-4 bg-gray-200 rounded-sm" />
                    <div className="w-5 h-4 bg-gray-200 rounded-sm" />
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Security ── */}
      {/* ── Enterprise infrastructure badge strip ── */}
      <section className="py-12 px-4 sm:px-6 bg-gray-950">
        <div className="max-w-5xl mx-auto">
          <div className="text-center text-xs font-semibold tracking-[0.25em] text-gray-500 uppercase mb-8">Enterprise-Grade Infrastructure</div>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6">
            {[
              { icon: Shield, label: 'HIPAA Compliant' },
              { icon: Lock, label: 'AES-256 Encryption' },
              { icon: CheckCircle2, label: 'BAA Included' },
              { icon: Eye, label: 'PHI Minimized' },
              { icon: FileText, label: 'Full Audit Logs' },
            ].map(b => (
              <div key={b.label} className="flex items-center gap-2.5 text-gray-400">
                <b.icon className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium">{b.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="security" className="py-20 lg:py-24 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-indigo-600" />
            </div>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-4">Security you can trust</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto mb-12">
              Built for healthcare from the ground up. Every layer — from how we handle recordings to how notes are stored — was designed with patient privacy first.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: Shield, label: 'HIPAA Compliant', sub: 'BAA with every customer' },
              { icon: Eye, label: 'PHI Minimized', sub: 'Identifiers tokenized before AI drafting' },
              { icon: Lock, label: 'Fully Encrypted', sub: 'In transit and at rest' },
              { icon: Brain, label: 'No Data Training', sub: 'Your data is never used' },
            ].map((s, i) => (
              <Reveal key={s.label} delay={i * 100}>
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:shadow-md transition h-full">
                  <s.icon className="w-7 h-7 text-indigo-500 mx-auto mb-3" />
                  <div className="font-semibold text-gray-900 mb-1">{s.label}</div>
                  <div className="text-sm text-gray-500">{s.sub}</div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={400}>
            <a href="/security" className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-700 font-medium text-sm mt-8 transition">
              Read full security overview <ArrowRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-20 lg:py-24 px-4 sm:px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-10">
              <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">Pricing</p>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">Simple, transparent pricing</h2>
              <p className="text-gray-500 mt-3">Two plans. No hidden fees. No per-note charges.</p>
            </div>
          </Reveal>
          <PricingCards navigate={navigate} />
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 lg:py-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="text-center mb-14">
              <p className="text-sm font-semibold text-indigo-500 uppercase tracking-wide mb-3">FAQ</p>
              <h2 className="text-3xl font-bold text-gray-900">Common questions</h2>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <div className="space-y-4">
              {[
                {
                  q: 'Does it work with my EMR?',
                  a: 'Yes. Dr. Scribey works with every EMR — Epic, Cerner, athenahealth, eClinicalWorks, DrChrono, ModMed, and more. Our Chrome extension detects your EMR and pushes notes directly into the right fields. No plugins to install, no IT setup, no API keys. Works out of the box.',
                },
                {
                  q: 'How accurate are the notes?',
                  a: 'Every note passes through a multi-layer AI safety review before you see it. First, the note is generated from your recording. Then an independent AI critic reviews it against the original audio — flagging anything that wasn\'t actually said. The AI will never fabricate clinical findings, invent exam details, or add information that wasn\'t in your dictation. You can trace every sentence in your note back to the exact moment in your recording. Over time, Dr. Scribey learns your personal documentation style and adapts — but accuracy always comes first.',
                },
                {
                  q: 'Is it really HIPAA compliant?',
                  a: 'Yes — HIPAA compliance is foundational, not an afterthought. We sign a Business Associate Agreement (BAA) with every customer. All audio and text are encrypted in transit (TLS 1.2+) and at rest (AES-256). Patient identifiers are tokenized before reaching our AI models, and we never store PHI in any AI system. Your data is never used to train AI models — it belongs to you. Our infrastructure runs on HIPAA-eligible AWS services with full audit logging on every AI interaction.',
                },
                {
                  q: 'What\'s the difference between Core and Pro?',
                  a: 'Core covers everything most physicians need — office visits, consults, rounding notes, patient letters, and direct EMR push via our Chrome extension. Pro adds op note generation from quick 30-second dictations and ICD-10/CPT coding suggestions. If you\'re a surgeon, Pro pays for itself with one properly coded procedure per month.',
                },
                {
                  q: 'Can I try it before paying?',
                  a: 'Absolutely. Every account starts with 10 free encounters — no credit card required. You get the full Pro experience during your trial so you can test op notes, coding, EMR push, and everything else before deciding.',
                },
                {
                  q: 'What specialties does it support?',
                  a: 'Over 80 specialties with 360+ procedure-specific note templates — from orthopedics and cardiology to psychiatry and ophthalmology. The AI is also specialty-aware, understanding field-specific exams and conventions.',
                },
                {
                  q: 'How does the free trial work?',
                  a: 'Sign up and get 10 free encounters — no credit card required, no time limit. Record, generate notes, and see how Dr. Scribey adapts to your style. When you\'re ready, subscribe to keep going.',
                },
                {
                  q: 'Can I cancel anytime?',
                  a: 'Yes. No contracts, no commitments. Cancel from your settings page and your subscription ends at the end of the billing period. You can export all your data before leaving.',
                },
                {
                  q: 'Does it work on my phone?',
                  a: 'Yes. Dr. Scribey works in any modern browser on desktop, tablet, or phone. Record encounters between patients on your iPhone or Android — no app download required.',
                },
              ].map((faq, i) => (
                <details key={i} className="group bg-white rounded-xl border border-gray-100 hover:shadow-sm transition">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="font-medium text-gray-900 pr-4">{faq.q}</span>
                    <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform flex-shrink-0" />
                  </summary>
                  <div className="px-5 pb-5 text-gray-500 leading-relaxed text-sm -mt-1">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 to-violet-700" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }} />
        <div className="relative max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Ready to stop charting at midnight?</h2>
            <p className="text-indigo-100 text-lg mb-10">Join physicians who are getting their evenings back.</p>
            <button onClick={() => navigate('/register')} className="bg-white text-indigo-600 hover:bg-indigo-50 font-semibold px-8 py-4 rounded-xl text-lg transition shadow-xl">
              Try 10 Encounters Free
            </button>
            <p className="text-indigo-200 text-sm mt-3">No credit card required</p>
          </Reveal>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-gray-900 text-gray-400 py-14 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-4 gap-10 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 bg-indigo-500 rounded-md flex items-center justify-center">
                  <Stethoscope className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-white font-bold">Dr. Scribey</span>
              </div>
              <p className="text-sm leading-relaxed">AI-powered clinical documentation for independent practices.</p>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-4">Product</h4>
              <ul className="space-y-2.5 text-sm">
                <li><a href="#features" className="hover:text-white transition">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Pricing</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition">How It Works</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-4">Legal</h4>
              <ul className="space-y-2.5 text-sm">
                <li><a href="/terms" className="hover:text-white transition">Terms of Service</a></li>
                <li><a href="/privacy" className="hover:text-white transition">Privacy Policy</a></li>
                <li><a href="/baa" className="hover:text-white transition">BAA</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-sm font-semibold mb-4">Security</h4>
              <ul className="space-y-2.5 text-sm">
                <li><a href="/security" className="hover:text-white transition">Security Overview</a></li>
                <li><a href="mailto:info@tailwindx.org" className="hover:text-white transition">Contact Us</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-sm text-center text-gray-500">
            © {new Date().getFullYear()} TailwindX, LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}
